import posthog from '@leyan/lytics';
import { useMemoizedFn, useRequest } from 'ahooks';
import { orderBy, sortBy } from 'lodash-es';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  PhraseAttachmentElement,
  PhrasesResourceElement,
  PhrasesRootData,
  PhrasesStickerElement,
  PhrasesTextElement,
  queryPhrasesV2,
} from 'services/api/phrase';
import { regionLanguageMap } from 'services/config/region';
import { resourceURLToFile } from 'utils/getImage';
import toLowerCase from 'utils/toLowerCase';
import { getVideoMetadata } from 'utils/validator/video';
import wait from 'utils/wait';
import { useSendFileImage } from 'views/Conversation/Chat/components/MessageEditor/Toolbar/ImageUpload';
import { useSendFileVideo } from 'views/Conversation/Chat/components/MessageEditor/Toolbar/VideoUpload/hooks/useUploadVideo';
import { v4 as uuidv4 } from 'uuid';
import { useCurrentConversation } from './conversation';
import useIM from './im/useIM';
import { storeScopesOverlapWithApplyScope } from 'views/Setting/Phrase/hooks/useGroupValid';
import { Phrases, UserDB } from 'lib/db';
import { useLiveQuery } from 'dexie-react-hooks';

export interface PhraseFilterValue {
  groupType?: Phrase.Group['groupType'];
  phraseGroupId?: string;
}

export type PhraseValue = Omit<PhrasesRootData, 'phraseMap'> & Phrases;

const PHRASE_API_CACHE_KEY = 'queryPhrasesV2';

export const useGetPhraseData = () => {
  return useRequest(
    async () => {
      const row = await UserDB.instance?.apiCacheTag
        .where('apiKey')
        .equals(PHRASE_API_CACHE_KEY)
        .first();

      return queryPhrasesV2({
        eTag: row?.etag,
      })
        .then(async (res) => {
          const data = res?.data?.data;
          if (data && data.eTag && UserDB.instance) {
            await UserDB.instance.transaction(
              'rw',
              UserDB.instance.phraseGroup,
              UserDB.instance.phrasesRoot,
              UserDB.instance.phrases,
              async () => {
                await Promise.all([
                  UserDB.instance?.phraseGroup.clear(),
                  UserDB.instance?.phrasesRoot.clear(),
                  UserDB.instance?.phrases.clear(),
                ]);
              },
            );

            if (data.phraseRoots?.length > 0) {
              // 缓存后端数据
              const { eTag, phraseGroups, phraseRoots, disabledRegion } = data;
              const phraseRootsItems = phraseRoots.map(({ phraseMap, ...other }) => ({ ...other }));
              const phraseItems = phraseRoots.reduce((prev, current) => {
                const channel = current.channel;
                Object.keys(current.phraseMap).forEach((region) => {
                  if (current.phraseMap[region]) {
                    prev.push({
                      ...current.phraseMap[region],
                      disabledRelate: disabledRegion?.[channel]?.regions?.includes(region) ?? false,
                    });
                  }
                });
                return prev;
              }, [] as Phrases[]);

              await UserDB.instance.transaction(
                'rw',
                UserDB.instance.phraseGroup,
                UserDB.instance.phrasesRoot,
                UserDB.instance.phrases,
                async () => {
                  await Promise.all([
                    UserDB.instance?.phraseGroup.bulkAdd(phraseGroups, {
                      allKeys: true,
                    }),
                    UserDB.instance?.phrasesRoot.bulkAdd(phraseRootsItems, {
                      allKeys: true,
                    }),
                    UserDB.instance?.phrases.bulkAdd(phraseItems, {
                      allKeys: true,
                    }),
                  ]);
                },
              );
              await UserDB.instance?.apiCacheTag.put(
                {
                  etag: eTag,
                  apiKey: PHRASE_API_CACHE_KEY,
                },
                PHRASE_API_CACHE_KEY,
              );
            }
          }
          return data;
        })
        .catch((err) => {
          // eTag 不变 后端返回304 取前端缓存值
          if (err?.message?.includes('304')) {
            console.log('query phrase cache 304');
          }
        });
    },
    {
      manual: true,
    },
  );
};

export const usePhraseOnChat = (
  channel: General.PlatformUpperCaseTunnel,
  region: General.UpperCaseRegion,
  storeId: Conversation.Conversation['storeId'],
) => {
  const [phraseFilter, setPhraseFilter] = useState<PhraseFilterValue | undefined>();

  const validGroups = useLiveQuery(
    async () => {
      // 查询当前平台分组
      const groups = await UserDB.instance?.phraseGroup.where({ channel }).toArray();
      return (
        groups?.filter(({ applyScope, enable }) => {
          return (
            storeScopesOverlapWithApplyScope(
              [
                {
                  platform: channel,
                  regionStoreScopes: [
                    {
                      region,
                      storeIds: [Number(storeId)],
                      storeCount: 1,
                    },
                  ],
                  storeCount: 1,
                },
              ],
              applyScope,
            ) && enable
          );
        }) ?? ([] as Phrase.Group[])
      );
    },
    [channel, region],
    [] as Phrase.Group[],
  );

  const phraseList = useLiveQuery(
    async () => {
      // 查询当前 region 的快捷短语数据
      const phrases = await UserDB.instance?.phrases.where({ region }).toArray();
      if (phrases) {
        // 关联查询
        const data = await Promise.all(
          phrases.map(async (phrase) => {
            const root = await UserDB.instance?.phrasesRoot
              .where({ id: phrase.phraseRootId, channel })
              .first();
            return {
              ...root,
              ...phrase,
            };
          }),
        );
        // 过滤掉查不到 root 的数据
        return orderBy(
          data.filter(
            (v) => v.channel && !!validGroups.find((g) => g.id === v.phraseGroupId),
          ) as PhraseValue[],
          ({ sortNumber }) => Number(sortNumber),
          'desc',
        );
      }
      return [];
    },
    [channel, region, validGroups],
    [] as PhraseValue[],
  );

  // 快捷短语分组, 过滤
  const phraseGroups = useMemo(() => {
    return sortBy(
      validGroups.filter((g) => {
        const sameGroupType = phraseFilter?.groupType && phraseFilter.groupType === g.groupType;

        if (!sameGroupType) {
          return false;
        }

        const hasPhraseRoots = !!phraseList?.find((phrase) => phrase.phraseGroupId === g.id);

        return hasPhraseRoots;
      }),
      'sortNumber',
    );
  }, [phraseFilter?.groupType, phraseList, validGroups]);

  // 快捷短语列表, 过滤
  const phrasesByGroup = useMemo(() => {
    if (phraseList) {
      return phraseList.filter((phrase) => {
        const sameGroupType =
          phraseFilter?.groupType && phraseFilter.groupType === phrase.groupType;
        const sameGroupId = phraseFilter?.phraseGroupId === phrase.phraseGroupId;

        return sameGroupType && sameGroupId;
      });
    }
    return [];
  }, [phraseList, phraseFilter]);

  // 更新过滤器值
  const updatePhraseFilter = useCallback(
    (value: PhraseFilterValue) => {
      setPhraseFilter({
        ...phraseFilter,
        ...value,
      });
    },
    [phraseFilter],
  );

  useEffect(() => {
    if (!phraseFilter?.groupType) {
      updatePhraseFilter({
        groupType: 'PERSONAL',
      });
    }
    if (phraseFilter?.groupType && !phraseFilter?.phraseGroupId && phraseGroups.length > 0) {
      updatePhraseFilter({
        phraseGroupId: phraseGroups[0].id,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phraseFilter?.groupType, phraseFilter?.phraseGroupId, phraseGroups]);

  /**
   * 是否启动联想
   */
  const isRelateRegion = useMemo(() => phraseList.some((v) => !v.disabledRelate), [phraseList]);

  return {
    phraseList,
    phraseFilter,
    updatePhraseFilter,
    phraseGroups,
    phrasesByGroup,
    isRelateRegion,
  };
};

export const useSendPhrase = (
  payload:
    | {
        onSend?: (type: 'text' | 'sticker' | 'image' | 'invite') => void;
      }
    | undefined,
  onMercadoChatView: boolean,
) => {
  const currentConversation = useCurrentConversation();
  const { sendText, sendSticker, sendAttachment } = useIM();
  const [sending, setSending] = useState(false);
  const sendFileImage = useSendFileImage();
  const sendFileVideo = useSendFileVideo();

  const sendPhrase = useMemoizedFn(async (phraseValue?: PhraseValue) => {
    if (!currentConversation || !phraseValue || sending) return;

    const { conversationId, region, channel } = currentConversation;

    setSending(true);
    const {
      phraseElems: { elems },
    } = phraseValue;

    const { [toLowerCase(region)]: targetLang } = regionLanguageMap;

    if (channel === 'MERCADO') {
      const { texts, attachments } = elems.reduce(
        (res, cur: any) => {
          if (cur.attachment) {
            res.attachments.push(cur);
          }
          if (cur.text) {
            res.texts.push(cur);
          }
          return res;
        },
        {
          texts: [] as PhrasesTextElement[],
          attachments: [] as PhraseAttachmentElement[],
        },
      );

      for (let i = 0; i < texts.length; i++) {
        const { content, translationContent } = texts[i].text;
        await sendAttachment(
          currentConversation,
          {
            text: content,
            subject: '',
            originalText: content,
            attachments:
              i === 0 ? attachments.map(({ attachment }) => attachment.attachments).flat() : [],
          },
          !translationContent || !translationContent.trim()
            ? undefined
            : {
                targetLanguage: targetLang as any,
                text: translationContent,
                status: 'success',
                source: 'sender',
              },
          onMercadoChatView,
        );
      }
    }

    for (const phraseEl of elems) {
      // 文本发送
      if ((phraseEl as PhrasesTextElement).text && channel !== 'MERCADO') {
        const textObj = (phraseEl as PhrasesTextElement).text;
        const { content, translationContent } = textObj;
        if (!translationContent || !translationContent.trim()) {
          if (content) {
            await sendText(conversationId, content);
          }
        } else {
          await sendText(conversationId, content, {
            autoTranslation: {
              targetLanguage: targetLang,
              text: translationContent,
              status: 'success',
              source: 'sender',
            } as Conversation.MsgText['autoTranslation'],
          });
        }
      }

      // 表情发送
      if ((phraseEl as PhrasesStickerElement).sticker) {
        const stickerObj = (phraseEl as PhrasesStickerElement).sticker;
        await sendSticker(conversationId, {
          stickerId: stickerObj.sid,
          stickerPackageId: stickerObj.pid,
        });
      }

      // 图片视频资源发送
      if ((phraseEl as PhrasesResourceElement).resource) {
        const resourceObj = (phraseEl as PhrasesResourceElement).resource;
        const { resourceType, localUrl, name } = resourceObj;
        if (resourceType === 1) {
          try {
            sendFileImage(
              localUrl,
              () => {
                payload?.onSend && payload.onSend?.('image');
              },
              name,
            );
          } catch (err) {
            setSending(false);
          }
        }

        if (resourceType === 2) {
          try {
            // 视频发送
            const { localUrl, name } = resourceObj;
            await wait(300);
            const file = await resourceURLToFile(localUrl, name, 'video/mp4');

            const { width, height } = await getVideoMetadata(localUrl);
            await sendFileVideo(file, width, height, uuidv4());
          } catch (err) {
            setSending(false);
          }
        }
      }

      await wait(300);
    }
    posthog.capture('$sendPhraseBehavior', {
      phraseRootId: phraseValue.phraseRootId,
      phraseId: phraseValue.id,
      conversationId,
      region,
    });
    setSending(false);
  });

  return sendPhrase;
};
